<template>
    <div class="bg-white p-3 rounded-lg shadow-md" data-testid="summary-wizard">
        <div class="flex justify-between items-center mb-3">
            <div>
                <h2 class="text-xl font-bold">New Summary Template</h2>
                <p class="text-xs text-gray-500">Guided setup — answer a few questions and I'll build it for you.</p>
            </div>
            <div class="flex items-center gap-3">
                <button @click="$emit('switch-to-builder')"
                    data-testid="btn-summary-wizard-advanced-builder"
                    class="text-xs text-primary hover:underline">
                    Use advanced builder instead
                </button>
                <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700"
                    data-testid="btn-close-summary-wizard">
                    <i class="pi pi-times text-xl"></i>
                </button>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <!-- Chat panel -->
            <div class="lg:col-span-3 flex flex-col border border-gray-200 rounded-lg bg-gray-50/50">
                <!-- Progress -->
                <div class="px-4 pt-3">
                    <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full bg-primary rounded-full transition-all duration-300"
                            :style="{ width: `${Math.min(100, (progress.answered / progress.total) * 100)}%` }"></div>
                    </div>
                </div>

                <!-- Transcript -->
                <div ref="chatScroll" class="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[20rem] max-h-[26rem]">
                    <template v-for="(entry, index) in transcript" :key="index">
                        <div class="flex">
                            <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-700 max-w-[85%] shadow-sm">
                                {{ entry.question }}
                            </div>
                        </div>
                        <div class="flex justify-end">
                            <button @click="jumpBackTo(index)" :disabled="saving"
                                :data-testid="`summary-wizard-answer-${index}`"
                                class="group bg-primary text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm
                                    max-w-[85%] shadow-sm text-left cursor-pointer hover:bg-primary/85 transition-colors"
                                title="Change this answer">
                                {{ entry.answer }}
                                <i class="pi pi-pencil text-[10px] ml-1.5 opacity-0 group-hover:opacity-70 transition-opacity"></i>
                            </button>
                        </div>
                    </template>

                    <!-- Current question -->
                    <div class="flex">
                        <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-700 max-w-[85%] shadow-sm"
                            data-testid="summary-wizard-current-question">
                            {{ currentQuestion }}
                        </div>
                    </div>
                </div>

                <!-- Input area -->
                <div class="border-t border-gray-200 bg-white rounded-b-lg px-4 py-3">
                    <!-- Step: name -->
                    <div v-if="currentStep === 'name'" class="flex gap-2">
                        <InputText v-model="nameInput" class="flex-1" autofocus
                            placeholder="e.g. CGPA Summary Sheet"
                            data-testid="summary-wizard-input-name" @keyup.enter="answerName" />
                        <Button icon="pi pi-send" :disabled="!nameInput.trim()" @click="answerName"
                            data-testid="summary-wizard-send-name" />
                    </div>

                    <!-- Step: method -->
                    <!-- Step: preset -->
                    <div v-else-if="currentStep === 'preset'" class="flex flex-wrap gap-2">
                        <button v-for="(config, key) in PRESET_CONFIGS" :key="key"
                            @click="answerPreset(key as any)"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors max-w-xs">
                            <span class="block text-sm font-medium text-gray-800">{{ config.label }}</span>
                            <span class="block text-[11px] text-gray-500">{{ config.description }}</span>
                        </button>
                        <button @click="answerPreset('custom')"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors max-w-xs">
                            <span class="block text-sm font-medium text-gray-800">Custom</span>
                            <span class="block text-[11px] text-gray-500">Configure everything manually</span>
                        </button>
                    </div>

                    <!-- Step: method (may be pre-filled by preset) -->
                    <div v-else-if="currentStep === 'method'" class="flex flex-wrap gap-2">
                        <button v-for="option in methodOptions" :key="option.value"
                            @click="answerMethod(option.value)"
                            :data-testid="`summary-wizard-method-${option.value}`"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors max-w-xs">
                            <span class="block text-sm font-medium text-gray-800">{{ option.label }}</span>
                            <span class="block text-[11px] text-gray-500">{{ option.description }}</span>
                        </button>
                    </div>

                    <!-- Step: reportMode -->
                    <div v-else-if="currentStep === 'reportMode'" class="flex flex-wrap gap-2">
                        <button @click="answerReportMode('semester')"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors max-w-xs">
                            <span class="block text-sm font-medium text-gray-800">Semester Report</span>
                            <span class="block text-[11px] text-gray-500">Previous / Current / Cumulative stats for one semester</span>
                        </button>
                        <button @click="answerReportMode('final_result')"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors max-w-xs">
                            <span class="block text-sm font-medium text-gray-800">Final Result</span>
                            <span class="block text-[11px] text-gray-500">All courses + scores across all semesters with cumulative calculation</span>
                        </button>
                    </div>

                    <!-- Step: scopes -->
                    <div v-else-if="currentStep === 'scopes'" class="space-y-2">
                        <div class="flex flex-wrap gap-2">
                            <button v-for="option in scopeOptions" :key="option.value"
                                @click="toggleScope(option.value)"
                                :data-testid="`summary-wizard-scope-${option.value}`"
                                class="text-xs px-3 py-1.5 rounded-full border transition-colors"
                                :class="selectedScopes.includes(option.value)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary'">
                                <i v-if="selectedScopes.includes(option.value)" class="pi pi-check mr-1 text-[10px]"></i>
                                {{ option.label }}
                            </button>
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                :disabled="selectedScopes.length === 0"
                                data-testid="summary-wizard-confirm-scopes" @click="confirmScopes" />
                        </div>
                    </div>

                    <!-- Step: headerStyle -->
                    <div v-else-if="currentStep === 'headerStyle'" class="flex flex-wrap gap-2">
                        <button v-for="option in headerStyleOptions" :key="option.value"
                            @click="answerHeaderStyle(option.value)"
                            :data-testid="`summary-wizard-header-style-${option.value}`"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors max-w-xs">
                            <span class="block text-sm font-medium text-gray-800">{{ option.label }}</span>
                            <span class="block text-[11px] text-gray-500">{{ option.description }}</span>
                        </button>
                    </div>

                    <!-- Step: stats depth -->
                    <div v-else-if="currentStep === 'stats'" class="flex flex-wrap gap-2">
                        <button @click="answerStats(true)" data-testid="summary-wizard-chip-stats-full" :class="chipClass">
                            Full working (TCP, TNUP, TNUR)
                        </button>
                        <button @click="answerStats(false)" data-testid="summary-wizard-chip-stats-compact" :class="chipClass">
                            Just GPA / CGPA
                        </button>
                    </div>

                    <!-- Step: outcomes -->
                    <div v-else-if="currentStep === 'outcomes'" class="space-y-2">
                        <div class="flex flex-wrap gap-2">
                            <button v-for="option in outcomeOptions" :key="option.key"
                                @click="toggleOutcome(option.key)"
                                :data-testid="`summary-wizard-outcome-${option.key}`"
                                class="text-xs px-3 py-1.5 rounded-full border transition-colors"
                                :class="selectedOutcomes.includes(option.key)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary'">
                                <i v-if="selectedOutcomes.includes(option.key)" class="pi pi-check mr-1 text-[10px]"></i>
                                {{ option.label }}
                            </button>
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                data-testid="summary-wizard-confirm-outcomes" @click="confirmOutcomes" />
                        </div>
                    </div>

                    <!-- Step: extras -->
                    <div v-else-if="currentStep === 'extras'" class="space-y-2">
                        <div class="flex flex-wrap gap-2">
                            <button v-for="option in extraOptions" :key="option.key"
                                @click="toggleExtra(option.key)"
                                :data-testid="`summary-wizard-extra-${option.key}`"
                                class="text-xs px-3 py-1.5 rounded-full border transition-colors"
                                :class="selectedExtras.includes(option.key)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary'"
                                v-tooltip.top="option.description">
                                <i v-if="selectedExtras.includes(option.key)" class="pi pi-check mr-1 text-[10px]"></i>
                                {{ option.label }}
                            </button>
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                data-testid="summary-wizard-confirm-extras" @click="confirmExtras" />
                        </div>
                    </div>

                    <!-- Step: displayOptions -->
                    <!-- Step: classification -->
                    <div v-else-if="currentStep === 'classification'" class="space-y-4">
                        <div>
                            <h4 class="text-xs font-semibold text-gray-600 mb-2">Graduating Categories</h4>
                            <div class="text-[10px] text-gray-400 mb-1">Label · Min Threshold · Status (e.g. Proceed to PhD — leave empty if not applicable)</div>
                            <div class="space-y-1">
                                <div v-for="(cat, idx) in answers.graduatingCategories" :key="'grad-'+idx" class="flex items-center gap-2">
                                    <InputText :modelValue="cat.label" @update:modelValue="v => { cat.label = v ?? ''; cat.key = (v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || cat.key; }" size="small" class="flex-1" placeholder="Label" />
                                    <InputNumber v-model="cat.min_threshold" size="small" class="w-20" placeholder="Min" :min-fraction-digits="0" :max-fraction-digits="2" />
                                    <InputText v-model="cat.status" size="small" class="flex-1" placeholder="Status (e.g. Proceed to PhD)" />
                                    <Button icon="pi pi-times" severity="danger" text rounded size="small"
                                        @click="answers.graduatingCategories.splice(idx, 1)" />
                                </div>
                            </div>
                            <Button label="Add Category" icon="pi pi-plus" text size="small" class="mt-1"
                                @click="answers.graduatingCategories.push({ label: '', key: 'custom_' + Date.now(), min_threshold: 0, status: null })" />
                        </div>

                        <div>
                            <h4 class="text-xs font-semibold text-gray-600 mb-2">Non-Graduating Statuses</h4>
                            <div class="space-y-1">
                                <div v-for="(cat, idx) in answers.nonGraduatingStatuses" :key="'nongrad-'+idx" class="flex items-center gap-2">
                                    <InputText :modelValue="cat.label" @update:modelValue="v => { cat.label = v ?? ''; cat.key = (v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/[^a-z0-9]$/g, '') || cat.key; }" size="small" class="flex-1" placeholder="Label" />
                                    <InputNumber v-model="cat.min_threshold" size="small" class="w-24" placeholder="Min" :min-fraction-digits="0" :max-fraction-digits="2" />
                                    <Button icon="pi pi-times" severity="danger" text rounded size="small"
                                        @click="answers.nonGraduatingStatuses.splice(idx, 1)" />
                                </div>
                            </div>
                            <Button label="Add Status" icon="pi pi-plus" text size="small" class="mt-1"
                                @click="answers.nonGraduatingStatuses.push({ label: '', key: 'custom_' + Date.now(), min_threshold: 0 })" />
                        </div>

                        <div class="grid grid-cols-2 gap-3">
                            <div class="flex flex-col gap-1">
                                <label class="text-xs font-medium text-gray-600">Graduating Label</label>
                                <InputText v-model="answers.graduatingStatusLabels.passing" size="small" placeholder="Graduating" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-xs font-medium text-gray-600">Not Graduating Prefix</label>
                                <InputText v-model="answers.graduatingStatusLabels.failingPrefix" size="small" placeholder="Not Graduating" />
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-medium text-gray-600">Degree Class Column</label>
                            <Select v-model="answers.degreeClassVisibility" :options="[
                                { label: 'Auto (final year only)', value: 'auto' },
                                { label: 'Always show', value: 'always' },
                                { label: 'Never show', value: 'never' },
                            ]" optionLabel="label" optionValue="value" size="small" class="w-full max-w-xs" />
                        </div>

                        <Button label="Confirm Classification" icon="pi pi-check" size="small" severity="success"
                            @click="confirmClassification()" />
                    </div>

                    <div v-else-if="currentStep === 'displayOptions'" class="space-y-3">
                        <!-- Campus column -->
                        <div class="space-y-2">
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.includeCampus" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Include Campus column (for schools with multiple campuses)
                            </label>
                        </div>
                        <!-- Signatures -->
                        <div class="space-y-2">
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.showSignatures" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Include signature section on PDF
                            </label>
                        </div>
                        <div v-if="displayOpts.showSignatures" class="bg-gray-50 rounded-lg p-3 space-y-2">
                            <p class="text-xs text-gray-500 font-medium">Signature titles:</p>
                            <div class="flex flex-wrap gap-1.5">
                                <span v-for="(title, idx) in displayOpts.signatureTitles" :key="idx"
                                    class="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white border border-gray-200 rounded-full">
                                    {{ title }}
                                    <button class="pi pi-times text-[9px] text-gray-400 hover:text-red-500" @click="removeSignatureTitle(idx)"></button>
                                </span>
                            </div>
                            <div class="flex gap-2">
                                <input v-model="displayOpts.signatureInput" type="text" placeholder="Add title..."
                                    class="text-xs px-2 py-1 border border-gray-200 rounded flex-1"
                                    @keyup.enter="addSignatureTitle" />
                                <button @click="addSignatureTitle" class="text-xs px-2 py-1 bg-primary text-white rounded">Add</button>
                            </div>
                        </div>
                        <!-- Graduation conditions -->
                        <div class="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                            <p class="text-xs text-amber-800 font-medium">Graduation conditions — which checks must pass for "Graduating" status?</p>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.checkFinalYear" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Must be in final year / extra year
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.checkOutstandingCourses" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Must have no outstanding compulsory courses
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" v-model="displayOpts.checkMinUnits" class="rounded border-gray-300 text-primary focus:ring-primary" />
                                Must meet minimum credit units requirement
                            </label>
                        </div>
                        <!-- PDF Header Config -->
                        <div class="mt-3 bg-primary/10 border border-primary/20 rounded-lg p-3 space-y-2">
                            <p class="text-xs text-primary-700 font-medium">PDF Header Overrides (leave blank for defaults)</p>
                            <div class="grid grid-cols-2 gap-2">
                                <div>
                                    <label class="block text-[10px] text-gray-500">School Name Override</label>
                                    <input v-model="displayOpts.pdfSchoolName" type="text" placeholder="e.g. THE REDEEMED COLLEGE OF MISSIONS..."
                                        class="text-xs px-2 py-1 border border-gray-200 rounded w-full" />
                                </div>
                                <div>
                                    <label class="block text-[10px] text-gray-500">Report Title Override</label>
                                    <input v-model="displayOpts.pdfReportTitle" type="text" placeholder="e.g. SUMMARY FOR PRESENTATION..."
                                        class="text-xs px-2 py-1 border border-gray-200 rounded w-full" />
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div>
                                    <label class="block text-[10px] text-gray-500">Semester Display</label>
                                    <select v-model="displayOpts.pdfSemesterFormat" class="text-xs px-2 py-1 border border-gray-200 rounded w-full">
                                        <option value="number">Number only (1, 2)</option>
                                        <option value="full">Full name (1st Semester)</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-[10px] text-gray-500">Logo Layout</label>
                                    <select v-model="displayOpts.pdfLogoPosition" class="text-xs px-2 py-1 border border-gray-200 rounded w-full">
                                        <option value="left">School logo left, Affiliate right</option>
                                        <option value="right">Affiliate left, School logo right</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label class="block text-[10px] text-gray-500">Level Display Map (for Diploma/Certificate — e.g. 100→DIPLOMA ONE)</label>
                                <div class="flex flex-wrap gap-1.5 mb-1">
                                    <span v-for="(display, lvl) in displayOpts.pdfLevelDisplayMap" :key="lvl"
                                        class="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-white border border-gray-200 rounded-full">
                                        {{ lvl }} → {{ display }}
                                        <button class="pi pi-times text-[8px] text-gray-400 hover:text-red-500" @click="delete displayOpts.pdfLevelDisplayMap[lvl]"></button>
                                    </span>
                                </div>
                                <div class="flex gap-1">
                                    <input v-model="displayOpts.pdfLevelMapInput" type="text" placeholder="100:DIPLOMA ONE"
                                        class="text-xs px-2 py-1 border border-gray-200 rounded flex-1"
                                        @keyup.enter="addLevelMapping" />
                                    <button @click="addLevelMapping"
                                        class="text-[10px] px-2 py-1 bg-primary text-white rounded">Add</button>
                                </div>
                            </div>
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                data-testid="summary-wizard-confirm-display-options" @click="confirmDisplayOptions" />
                        </div>
                    </div>

                    <!-- Step: review -->
                    <div v-else-if="currentStep === 'review'" class="space-y-3">
                        <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
                            <p><span class="text-gray-500">Summary sheet:</span> <strong>{{ answers.name }}</strong></p>
                            <p>
                                <span class="text-gray-500">Method:</span>
                                {{ answers.calculationMethod === 'cgpa' ? 'CGPA (grade points)' : 'Weighted (score × units)' }}
                            </p>
                            <p>
                                <span class="text-gray-500">Columns:</span>
                                {{ reviewColumns.length }} —
                                {{ reviewColumns.map((c) => c.label).slice(0, 8).join(' · ') }}{{ reviewColumns.length > 8 ? ' …' : '' }}
                            </p>
                        </div>

                        <div class="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 text-xs text-primary-700">
                            <i class="pi pi-info-circle mr-1"></i>
                            One template works for every class or programme — after creating, use
                            <strong>Generate</strong> on it in the list to run the sheet for any term.
                            Need fine-tuning later? <strong>Edit</strong> opens the advanced builder.
                        </div>

                        <div class="flex justify-end">
                            <Button :label="saving ? 'Creating…' : 'Create Summary Template'" icon="pi pi-check"
                                :loading="saving" data-testid="btn-summary-wizard-create" @click="saveTemplate" />
                        </div>
                    </div>

                    <!-- Back -->
                    <div v-if="transcript.length > 0 && !saving" class="mt-2 flex items-center justify-between">
                        <button @click="goBack" class="text-[11px] text-gray-400 hover:text-gray-600"
                            data-testid="summary-wizard-back">
                            <i class="pi pi-undo mr-1"></i>Back to previous question
                        </button>
                        <span class="text-[11px] text-gray-300">or click any answer above to change it</span>
                    </div>
                </div>
            </div>

            <!-- Live preview -->
            <div class="lg:col-span-2">
                <SummaryWizardPreview :answers="previewAnswers" :rows="previewRows" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useSummaryWizard } from '@/composables/useSummaryWizard';
import SummaryWizardPreview from './SummaryWizardPreview.vue';

const emit = defineEmits(['close', 'saved', 'switch-to-builder']);

const {
  answers,
  transcript,
  saving,
  nameInput,
  selectedScopes,
  selectedOutcomes,
  selectedExtras,
  currentStep,
  currentQuestion,
  progress,
  methodOptions,
  scopeOptions,
  headerStyleOptions,
  outcomeOptions,
  extraOptions,
  displayOpts,
  addSignatureTitle,
  removeSignatureTitle,
  addLevelMapping,
  previewAnswers,
  previewRows,
  reviewColumns,
  answerName,
  answerMethod,
  toggleScope,
  confirmScopes,
  answerHeaderStyle,
  answerStats,
  toggleOutcome,
  confirmOutcomes,
  toggleExtra,
  confirmExtras,
  confirmDisplayOptions,
  answerPreset,
  answerReportMode,
  confirmClassification,
  PRESET_CONFIGS,
  goBack,
  jumpBackTo,
  saveTemplate,
} = useSummaryWizard(emit);

const chipClass = 'text-sm px-3 py-1.5 rounded-full border border-primary/40 text-primary bg-primary/5 hover:bg-primary hover:text-white transition-colors';

// Keep the newest message in view as the conversation grows.
const chatScroll = ref<HTMLElement | null>(null);
watch(
  () => [transcript.value.length, currentStep.value],
  async() => {
    await nextTick();
    chatScroll.value?.scrollTo({ top: chatScroll.value.scrollHeight, behavior: 'smooth' });
  },
);
</script>
